/**
 * Validation Toast Messages
 *
 * Maps validation error strings from validateRecord() / validateRecordAll()
 * to operator-friendly toast messages. Keeps all user-facing copy in one place.
 */

import { customToast } from './toast';
import { validateRecordAll } from './types';
import type { MasterFlightRecord } from './types';

export type ValidationSection = 'basic' | 'stations' | 'all';

interface ValidationMessage {
  pattern: RegExp;
  toast: string;
  section: 'basic' | 'stations';
}

const VALIDATION_MESSAGES: ValidationMessage[] = [
  // Flight Information (basic tab)
  { pattern: /^Flight Owner/i, toast: 'Missing: Flight Owner', section: 'basic' },
  { pattern: /^Flight Number/i, toast: 'Missing: Flight Number', section: 'basic' },
  { pattern: /^Operational Suffix/i, toast: 'Invalid: Operational Suffix must be 1 character', section: 'basic' },
  { pattern: /^Service Type/i, toast: 'Missing: Service Type', section: 'basic' },
  { pattern: /^Variant ID/i, toast: 'Missing: Variant ID', section: 'basic' },

  // Schedule Period (basic tab)
  { pattern: /^Season ID/i, toast: 'Missing: Season ID', section: 'basic' },
  { pattern: /^Start Date/i, toast: 'Missing: Start Date', section: 'basic' },
  { pattern: /^End Date.*required/i, toast: 'Missing: End Date', section: 'basic' },
  { pattern: /^End Date.*after/i, toast: 'Invalid: End Date must be after Start Date', section: 'basic' },

  // Frequency (basic tab)
  { pattern: /frequency.*0 operating dates/i, toast: 'No operating dates — the selected days don\'t fall within this date range. Adjust the frequency or dates.', section: 'basic' },
  { pattern: /frequency|At least one day/i, toast: 'Missing: Operating Days', section: 'basic' },

  // Schedule Status (basic tab)
  { pattern: /^Schedule Status/i, toast: 'Missing: Schedule Status', section: 'basic' },

  // Stations (stations tab) — non-sector-specific
  { pattern: /station sector/i, toast: 'Missing: At least 1 station sector is required', section: 'stations' },
];

/**
 * Dynamic sector error patterns.
 * Extracts sector number and maps departure/arrival to Station 1/Station 2.
 */
const SECTOR_PATTERNS: { pattern: RegExp; toastFn: (sector: number) => string }[] = [
  { pattern: /^Sector (\d+).*departureStation/i, toastFn: (s) => `Missing: Sector ${s} — Station 1` },
  { pattern: /^Sector (\d+).*arrivalStation/i, toastFn: (s) => `Missing: Sector ${s} — Station 2` },
  { pattern: /^Sector (\d+).*STD/i, toastFn: (s) => `Missing: Sector ${s} — STD` },
  { pattern: /^Sector (\d+).*STA/i, toastFn: (s) => `Missing: Sector ${s} — STA` },
  { pattern: /^Sector (\d+).*UTC.*Dep/i, toastFn: (s) => `Invalid: Sector ${s} — UTC 1` },
  { pattern: /^Sector (\d+).*UTC.*Arr/i, toastFn: (s) => `Invalid: Sector ${s} — UTC 2` },
];

/**
 * Converts a raw validation error into an operator-friendly toast message.
 * Falls back to the raw error if no match is found.
 */
export function getToastMessage(validationError: string): string {
  // Check static messages first
  for (const { pattern, toast } of VALIDATION_MESSAGES) {
    if (pattern.test(validationError)) {
      return toast;
    }
  }

  // Check dynamic sector patterns
  for (const { pattern, toastFn } of SECTOR_PATTERNS) {
    const match = validationError.match(pattern);
    if (match) {
      return toastFn(parseInt(match[1], 10));
    }
  }

  return validationError;
}

/**
 * Determines which section a validation error belongs to.
 */
function getErrorSection(error: string): 'basic' | 'stations' {
  // Check static messages
  const staticMatch = VALIDATION_MESSAGES.find(m => m.pattern.test(error));
  if (staticMatch) return staticMatch.section;

  // Sector errors are always stations
  if (/^Sector \d+/i.test(error)) return 'stations';

  return 'basic';
}

/**
 * Shows a cascade of toast messages for validation errors on a record.
 * @param record - The flight record to validate
 * @param section - Which section to show errors for: 'basic', 'stations', or 'all'
 */
export function showValidationToasts(record: MasterFlightRecord, section: ValidationSection = 'all'): void {
  const errors = validateRecordAll(record);
  if (errors.length === 0) return;

  const seen = new Set<string>();
  for (const error of errors) {
    if (section !== 'all' && getErrorSection(error) !== section) continue;

    const msg = getToastMessage(error);
    if (!seen.has(msg)) {
      seen.add(msg);
      customToast.error(msg);
    }
  }
}
